import {
  countryName,
  findTowns,
  formatRange,
  getTypical,
  type Typical,
} from '@trippath/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apart } from '../../lib/map';
import type { StopStart } from '../../components/stop/StopDialog';
import type { MapPlace } from '../../components/map/TripMap';

/** A stop of the plan already at the place clicked. */
export interface Visit {
  id: string;
  /** Its place in the plan, from 1. */
  number: number;
  nights: number;
}

interface Props {
  place: MapPlace;
  /** The plan's stops already there. */
  visits?: Visit[];
  /** Where a way here would leave from: the draft's last place, or the plan's last stop. */
  from?: string | null;
  /** Of how many stops the plan is. */
  stops?: number;
  /** Drafts a way here, on from `from`; a country has none, nor a trip read only. */
  onRoute?: (() => void) | undefined;
  /** Adds it as a stop, in a form to finish; none on a trip read only. */
  onStop?: (() => void) | undefined;
  /** Opens a stop already there. */
  onOpen?: (id: string) => void;
  onClose: () => void;
}

/**
 * A stop to start from a place clicked: a city as the nearest town of that name (its country
 * then known), else where it was clicked; a country to name a city in. Its typical prices come
 * with it on a trip in euros.
 */
export async function stopStart(place: MapPlace, currency: string): Promise<StopStart> {
  let start: StopStart = { city: '', country: place.country, place: null, typical: null };
  if (place.kind === 'city') {
    // An airport by its code: TAY is Tartu, its country known.
    const found = await findTowns(place.code ?? place.name, new AbortController().signal).then(
      (answer) => answer.towns.filter((town) => apart(town, place) < 1),
      () => [],
    );
    const [town] = found.toSorted((a, b) => apart(a, place) - apart(b, place));
    start = town
      ? {
          ...start,
          city: town.name,
          country: town.country,
          place: { lat: town.lat, lon: town.lon },
        }
      : { ...start, city: place.name, place: { lat: place.lat, lon: place.lon } };
  }
  const country = start.country;
  if (country === null || currency !== 'EUR') return start;
  const typical = await getTypical(country).catch(() => null);
  return { ...start, typical };
}

/** What a day there typically costs: a bed, food, getting around and an activity. */
function perDay(typical: Typical) {
  const parts = [typical.bed, typical.food, typical.local, typical.fun];
  const sum = (pick: 'min' | 'max') => parts.reduce((total, r) => total + r[pick], 0);
  return {
    min: { minor: sum('min'), currency: 'EUR' },
    max: { minor: sum('max'), currency: 'EUR' },
  };
}

/**
 * A place name clicked on the plan's map, beside where it was clicked: a city goes on the route
 * being drafted or becomes a stop; a country becomes a stop to name, with what a day there costs.
 */
export function MapPick({
  place,
  visits = [],
  from = null,
  stops = 0,
  onRoute,
  onStop,
  onOpen,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const first = useRef<HTMLButtonElement>(null);
  const [typical, setTypical] = useState<{ country: string; typical: Typical } | null>(null);

  useEffect(() => {
    first.current?.focus();
  }, [place]);

  // A press anywhere else closes it: on the map, which moves it away from where it was, on
  // another name, which opens its own, or on a widget.
  useEffect(() => {
    const away = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('.map-pick') !== null) return;
      onClose();
    };
    document.addEventListener('pointerdown', away);
    return () => {
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);

  useEffect(() => {
    const code = place.country;
    if (code === null) return;
    getTypical(code).then(
      (found) => {
        setTypical({ country: code, typical: found });
      },
      () => undefined,
    );
  }, [place.country]);

  const known = typical !== null && typical.country === place.country ? typical.typical : null;
  const city = place.kind === 'city';
  const [visit] = visits;
  // The first button takes the focus: the stop when it is in the plan, else the way here.
  const action = (
    key: string,
    title: string,
    detail: string,
    onClick: () => void,
    primary: boolean,
  ) => (
    <button
      key={key}
      ref={primary ? first : undefined}
      type="button"
      className={primary ? 'map-pick-action map-pick-primary' : 'map-pick-action'}
      onClick={onClick}
    >
      <strong>{title}</strong> <span>{detail}</span>
    </button>
  );
  return (
    <div
      className="map-pick"
      role="group"
      aria-label={place.name}
      // Beside the click, kept inside the map.
      style={{
        left: `min(${String(place.x + 12)}px, calc(100% - 296px))`,
        top: `min(${String(place.y + 12)}px, calc(100% - 260px))`,
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="map-pick-head">
        <p className="map-pick-name">
          <strong>{place.name}</strong>
          {place.country !== null && countryName(place.country, lang) !== place.name && (
            <span className="quiet"> · {countryName(place.country, lang)}</span>
          )}
        </p>
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('mapPick.close')}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {visit && <p className="map-pick-in">{t('mapPick.inPlan', { count: visits.length })}</p>}
      {known && (
        <p className="quiet map-pick-cost">
          {t('mapPick.perDay', { range: formatRange(perDay(known), lang, true) })}
        </p>
      )}
      <div className="map-pick-actions">
        {visits.map((v, at) =>
          action(
            v.id,
            t('mapPick.open'),
            t('mapPick.stopOf', { number: v.number, of: stops, count: v.nights }),
            () => {
              onOpen?.(v.id);
            },
            at === 0,
          ),
        )}
        {city &&
          onRoute &&
          action(
            'route',
            t('mapPick.wayHere'),
            from === null ? t('mapPick.wayHereFirst') : t('mapPick.wayHereFrom', { from }),
            onRoute,
            visit === undefined,
          )}
        {onStop &&
          action(
            'stop',
            visit ? t('mapPick.again') : t('mapPick.asStop'),
            t('mapPick.asStopDetail'),
            onStop,
            visit === undefined && !city,
          )}
      </div>
    </div>
  );
}
